import { updateOrderV2 } from '@api/order';
import { OrderStatus } from '@components/contants';
import PopupComponent from '@components/popup';
import { TOrder } from '@components/types';
import { getAddress, humanizeCurrency, trimToLength } from '@core/utils';
import { message } from 'antd';
import { CircleCheck, Clock, Printer, RefreshCcw, ShoppingBag, Wallet } from 'lucide-react';
import moment from 'moment';
import Link from 'next/link';
import { useState } from 'react';
import Ordertracker from '../track/ordertracker';


type Props = {
    editable?: boolean
    order: TOrder
    setOrderStage?: Function
    setDetailOrder?: Function
    noLink?: boolean
    showTracking?: boolean
    reloadFunction?: Function
};


export const OrderSummaryCard = ({ order, setOrderStage, setDetailOrder, noLink = false, showTracking = true, reloadFunction }: Props) => {
    const [openTrackOrder, setOpenTrackOrder] = useState(false);
    const [loading, setLoading] = useState(false);
    const [openReceivedConfirmModal, setopenReceivedConfirmModal] = useState(false)

    const handleReceived = () => {
        setLoading(true);
        updateOrderV2({ ...order, status: OrderStatus.COMPLETED })?.then((res) => {
            message.success("Order received successfully");
            setopenReceivedConfirmModal(false);
            reloadFunction?.();
        })?.catch((e) => {
            message.error("Failed to complete order");
        })?.finally(() => {
            setLoading(false);
        })
    }

    const isCompleted = order?.status === "Completed";
    const isCancelled = order?.status === "Cancelled";

    return (
        <div className="bg-white flex flex-col gap-3 p-4 rounded-2xl shadow-sm border border-neutral-200/70" data-order-id={order?.id}>
            <div className="flex flex-col gap-2 font-manrope border-b border-neutral-100 pb-3">
                <div className="flex justify-between items-start gap-2">
                    {noLink ? <h2 onClick={() => { setDetailOrder && setDetailOrder(order); setOrderStage && setOrderStage("detailorder") }} className="text-base font-bold text-neutral-800 flex flex-col gap-0.5 cursor-pointer">Order No. {order?.short_id || "N/A"}
                        <span className='text-xs font-medium text-neutral-500'>{order?.store?.name || "N/A"}</span>
                    </h2> :
                        <Link href={`/order/me/${order?.id}`} prefetch={false}><h2 className="text-base font-bold text-neutral-800 flex flex-col gap-0.5">Order No. {order?.short_id || "N/A"}
                            <span className='text-xs font-medium text-neutral-500'>{order?.store?.name || "N/A"}</span></h2></Link>}
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {
                            order.delivery_mode == "Delivery" ? <span className="bg-business-100 rounded-full px-2.5 py-1 text-xs font-semibold text-business-500">DELIVERY</span> :
                                <span className="bg-brand-100 rounded-full px-2.5 py-1 text-xs font-semibold text-brand-500">PICKUP</span>
                        }
                        <button className='text-neutral-400 hover:text-neutral-600 transition-colors' onClick={() => { reloadFunction?.(true); }}><RefreshCcw size={14} /></button>
                    </div>
                </div>
                <p className="text-xs text-neutral-500 font-manrope">{getAddress(order?.store?.address)}</p>
                <div className='text-xs text-neutral-500'>Bill ID: <span className='font-semibold text-neutral-700'>{order?.bill_id || "N/A"}</span></div>
                {(order?.info?.delivery_date && !isCompleted && !isCancelled) && <div className='text-success-500 text-xs font-medium flex items-center gap-1'><Clock size={12} /> Delivery on: <span className='font-semibold'>{moment(order?.info?.delivery_date).format("DD MMM, h:mm A")}</span></div>}
                <div className="flex justify-between items-center pt-1">
                    <span className="text-lg text-business-500 font-bold">{humanizeCurrency(order?.payable)}</span>
                    <div className="flex flex-row items-center gap-1.5">
                        <p className="text-xs text-neutral-500 font-medium flex gap-1 items-center"><Wallet size={13} /><span>{order?.payment_mode}</span></p>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${order?.is_paid ? "text-success-500 bg-success-100" : "text-error-500 bg-error-100"}`}>
                            {order?.is_paid ? "Paid" : "Pending"}
                        </span>
                    </div>
                </div>
                <h2 className="text-xs text-neutral-400">{order.updated_on != null ? `${moment(order?.updated_on).format("MMMM DD, YYYY h:mm A")}` : moment(order?.added_on).format("MMMM DD, YYYY | h:mm A")}</h2>
                <div className="flex flex-col gap-1.5 items-center justify-between">
                    {
                        order.orderitems.map((oi) => {
                            return <div className="flex flex-row justify-between items-center w-full px-3 py-2 bg-neutral-50 rounded-xl text-sm">
                                <span className="text-neutral-700 font-medium">{trimToLength(oi?.inventory?.product?.name, 50)}</span>
                                <span className="text-neutral-500 font-semibold flex-shrink-0 pl-2">×{oi.quantity}</span>
                            </div>
                        })
                    }
                </div>
            </div>
            <div className="flex flex-col gap-2">
                <div className="flex justify-between items-center flex-wrap gap-2">
                    <div className='flex items-center gap-1'>
                        {order?.store?.storemeta?.settings?.user_receieved_order ?
                            <button onClick={() => { setopenReceivedConfirmModal(true); }} className="text-xs px-3 py-1.5 text-neutral-50 bg-brand-500 rounded-full font-semibold font-manrope cursor-pointer hover:brightness-110 transition-all">RECEIVED</button>
                            :
                            <h2 className="flex gap-1 items-center text-sm font-medium">{isCompleted ? <CircleCheck size={16} className="text-success-500" /> : <ShoppingBag size={16} className="text-brand-500" />} <span className={`font-manrope font-semibold ${isCompleted ? "text-success-500" : "text-neutral-700"}`}>{noLink ? "" : "Order"} {order?.status}</span></h2>}
                    </div>
                    <div className="flex items-center gap-2">
                        {showTracking && <button
                            onClick={() => { setOpenTrackOrder(true); }}
                            className='text-xs px-3 py-1.5 border border-brand-500 text-brand-500 bg-white rounded-full font-semibold font-manrope whitespace-nowrap cursor-pointer hover:bg-brand-500/5 transition-colors'>
                            <span className="md:inline hidden">TRACK ORDER</span>
                            <span className="inline md:hidden">TRACK</span>
                        </button>}
                        <Link
                            target='_blank'
                            href={order?.store?.storemeta?.settings?.order_bill_page_url?.length > 0 ? `/${order?.store?.storemeta?.settings?.order_bill_page_url}/${order?.id}` : `/order/${order?.id}`}
                            prefetch={false}
                        >
                            <button className={`p-1.5 border border-business-500 text-business-500 bg-white rounded-full font-medium font-manrope cursor-pointer hover:bg-business-500/5 transition-colors`}><Printer size={13} /></button>
                        </Link>
                        <button className={`text-xs px-3 py-1.5 text-neutral-50 bg-brand-500 rounded-full font-semibold font-manrope cursor-pointer hover:brightness-110 transition-all ${isCompleted ? "" : "hidden"}`}>REORDER</button>
                    </div>
                </div>
            </div>
            {showTracking && <Ordertracker
                showViewDetails={openTrackOrder}
                setShowViewDetails={setOpenTrackOrder}
                order={order}
            />}
            <PopupComponent
                showModal={openReceivedConfirmModal}
                setShowModal={setopenReceivedConfirmModal}
                loading={loading}
                title="Received Order"
                message="Are you sure you want to received this order? If you received the order, please click on confirm."
                ActionFun={handleReceived}
                btnName="Confirm"
                closeBtnName="Cancel"
            />
        </div>
    )
}